import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { usePageTitle } from "@Hooks/usePageTitle";
import * as yup from "yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  Spinner,
} from "@chakra-ui/react";
import {
  IconUpload,
  IconFileSpreadsheet,
  IconX,
  IconAlertTriangle,
  IconArrowLeft,
} from "@tabler/icons-react";
import { AppInput } from "@Components/Input";
import { Button } from "@Components/Button";
import { ProdutoModal } from "@Components/ProdutoModal";
import { AcessoNegadoModal } from "@Components/AcessoNegadoModal";
import { LoadingScreen, ESTUDO_STEPS } from "@Components/LoadingScreen";
import useToast from "@Components/Toast";
import { maskCnpj, unmaskCnpj } from "@Utils/Masks/cnpj";
import { postAnalyzeFile, postClienteAnalyze, AnalyzeFileResponse } from "@Api/estudo";
import { myClientsApi, MyClient } from "@Api/myClients";
import { useAcesso } from "@Hooks/useAcesso";

const ACCEPTED = [".csv", ".txt", ".xlsx", ".xls"];
const ACCEPT_ATTR = ".csv,.txt,.xlsx,.xls";
const MAX_BYTES = 10 * 1024 * 1024;

// Acima deste limite não listamos tudo — usamos busca no servidor.
const INLINE_LIMIT = 30;
// Página inicial usada só para descobrir o total (totalItens).
const PROBE_SIZE = 30;

type Modo = "prospect" | "cliente";

const hasValidExt = (name: string) =>
  ACCEPTED.some((ext) => name.toLowerCase().endsWith(ext));

const validateFile = (f: File): string | null => {
  if (!hasValidExt(f.name)) {
    return "Formato não suportado. Use .csv, .txt, .xlsx ou .xls.";
  }
  if (f.size > MAX_BYTES) {
    return "Arquivo muito grande. O limite é 10 MB.";
  }
  return null;
};

/**
 * Dropzone reutilizável (mesma UI do fluxo de prospect). Controlada pelo pai via
 * `file`/`onPick`, para servir tanto ao modo transportadora quanto ao upload de
 * planilha de um cliente da base.
 */
interface DropzoneProps {
  file: File | null;
  onPick: (f: File | null) => void;
  fileError: string | null;
  isDisabled?: boolean;
  required?: boolean;
}

const Dropzone: FC<DropzoneProps> = ({
  file,
  onPick,
  fileError,
  isDisabled,
  required = true,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    onPick(e.dataTransfer.files?.[0] ?? null);
  };

  return (
    <Box>
      <Text
        fontSize="12px"
        fontWeight="500"
        color="neutrals.text.strong-950"
        fontFamily="body"
        mb="4px"
      >
        Arquivo de abastecimentos
        {required && (
          <Text as="span" color="primary.light.base" ml="2px">
            *
          </Text>
        )}
      </Text>

      {file ? (
        <Flex
          align="center"
          justify="space-between"
          px="16px"
          py="14px"
          borderRadius="8px"
          border="1px solid"
          borderColor="neutrals.stroke.soft-200"
          bg="neutrals.surface.surface-0"
        >
          <HStack spacing={3} minW={0}>
            <Icon as={IconFileSpreadsheet} boxSize={5} color="primary.light.base" />
            <Box minW={0}>
              <Text
                fontSize="sm"
                fontFamily="body"
                color="neutrals.text.strong-950"
                isTruncated
              >
                {file.name}
              </Text>
              <Text fontSize="xs" color="neutrals.text.soft-400">
                {(file.size / 1024).toLocaleString("pt-BR", {
                  maximumFractionDigits: 0,
                })}{" "}
                KB
              </Text>
            </Box>
          </HStack>
          {!isDisabled && (
            <Icon
              as={IconX}
              boxSize={4}
              color="neutrals.icon.soft-400"
              cursor="pointer"
              onClick={() => onPick(null)}
              aria-label="Remover arquivo"
            />
          )}
        </Flex>
      ) : (
        <Flex
          direction="column"
          align="center"
          justify="center"
          gap={2}
          px="16px"
          py="28px"
          borderRadius="8px"
          border="1.5px dashed"
          borderColor={dragging ? "primary.light.base" : "neutrals.stroke.soft-200"}
          bg={dragging ? "primary.light.alpha-10" : "neutrals.background.weak-50"}
          cursor="pointer"
          transition="all 0.15s"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
        >
          <Icon as={IconUpload} boxSize={6} color="neutrals.icon.soft-400" />
          <Text fontSize="sm" fontFamily="body" color="neutrals.text.sub-600">
            Arraste o arquivo ou{" "}
            <Text as="span" color="primary.light.base" fontWeight="600">
              clique para selecionar
            </Text>
          </Text>
          <Text fontSize="xs" color="neutrals.text.soft-400">
            .csv, .txt, .xlsx ou .xls — até 10 MB
          </Text>
        </Flex>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTR}
        style={{ display: "none" }}
        onChange={(e) => onPick(e.target.files?.[0] ?? null)}
      />

      {fileError && (
        <HStack mt="6px" spacing={1}>
          <Icon as={IconAlertTriangle} boxSize={3.5} color="states.error.base" />
          <Text fontSize="11px" color="states.error.base" fontFamily="body">
            {fileError}
          </Text>
        </HStack>
      )}
    </Box>
  );
};

// -----------------------------------------------------------------------------
// Modo "Nova transportadora" (prospect / fora da base)
// -----------------------------------------------------------------------------

interface ProspectFormValues {
  razaoSocial: string;
  cnpj: string;
}

// react-hook-form sem @hookform/resolvers (não instalado): validamos com yup
// manualmente no submit e devolvemos o primeiro erro por campo.
const prospectSchema = yup.object({
  razaoSocial: yup.string().trim().required("Informe a razão social"),
  cnpj: yup.string().test("cnpj-len", "CNPJ deve ter 14 dígitos", (v) => {
    if (!v) return true;
    return unmaskCnpj(v).length === 14;
  }),
});

const ProspectMode: FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [acessoNegado, setAcessoNegado] = useState(false);

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ProspectFormValues>({
    defaultValues: { razaoSocial: "", cnpj: "" },
  });

  const [askProduto, setAskProduto] = useState(false);
  const lastVarsRef = useRef<{
    file: File;
    razaoSocial: string;
    cnpj?: string;
  } | null>(null);

  const mutation = useMutation<
    AnalyzeFileResponse,
    any,
    { file: File; razaoSocial: string; cnpj?: string; produto?: string }
  >({
    mutationFn: postAnalyzeFile,
    onSuccess: (data, variables) => {
      navigate(`/resultado/${data.token}`, {
        state: {
          fresh: data,
          razaoSocial: variables.razaoSocial,
          cnpj: variables.cnpj,
        },
      });
    },
    onError: (err: any, variables) => {
      if (err?.response?.data?.error === "produto_ausente") {
        lastVarsRef.current = variables;
        setAskProduto(true);
        return;
      }
      if (err?.response?.status === 403) {
        setAcessoNegado(true);
        return;
      }
      const msg =
        err?.response?.data?.error ||
        "Não foi possível analisar o arquivo. Tente novamente.";
      toast({ type: "error", message: msg });
    },
  });

  const pickFile = (f: File | null) => {
    if (!f) {
      setFile(null);
      return;
    }
    const err = validateFile(f);
    if (err) {
      setFile(null);
      setFileError(err);
      return;
    }
    setFileError(null);
    setFile(f);
  };

  const onSubmit = async (values: ProspectFormValues) => {
    try {
      await prospectSchema.validate(values, { abortEarly: false });
    } catch (e) {
      if (e instanceof yup.ValidationError) {
        e.inner.forEach((issue) => {
          if (issue.path) {
            setError(issue.path as keyof ProspectFormValues, {
              message: issue.message,
            });
          }
        });
        return;
      }
    }

    if (!file) {
      setFileError("Envie o arquivo de abastecimentos da transportadora.");
      return;
    }

    mutation.mutate({
      file,
      razaoSocial: values.razaoSocial.trim(),
      cnpj: values.cnpj ? unmaskCnpj(values.cnpj) : undefined,
    });
  };

  const isLoading = mutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {isLoading && (
        <LoadingScreen message="Gerando o estudo…" steps={ESTUDO_STEPS} />
      )}
      <VStack spacing="16px" align="stretch">
        <Controller
          control={control}
          name="razaoSocial"
          render={({ field }) => (
            <AppInput
              label="Razão social"
              isRequired
              placeholder="Nome da transportadora"
              value={field.value}
              onChange={field.onChange}
              hasError={!!errors.razaoSocial}
              hintText={errors.razaoSocial?.message}
              isDisabled={isLoading}
            />
          )}
        />

        <Controller
          control={control}
          name="cnpj"
          render={({ field }) => (
            <AppInput
              label="CNPJ"
              placeholder="00.000.000/0000-00"
              value={field.value}
              onChange={(v) => field.onChange(maskCnpj(v))}
              hasError={!!errors.cnpj}
              hintText={errors.cnpj?.message}
              isDisabled={isLoading}
            />
          )}
        />

        <Dropzone
          file={file}
          onPick={pickFile}
          fileError={fileError}
          isDisabled={isLoading}
        />

        <Button
          colorSchema="brand"
          variant="solid"
          type="submit"
          isLoading={isLoading}
          loadingText="Analisando…"
          spinner={<Spinner size="sm" />}
          mt="4px"
        >
          Efetuar análise
        </Button>
      </VStack>

      <ProdutoModal
        isOpen={askProduto}
        onClose={() => setAskProduto(false)}
        onConfirm={(produto) => {
          setAskProduto(false);
          if (lastVarsRef.current) mutation.mutate({ ...lastVarsRef.current, produto });
        }}
      />

      <AcessoNegadoModal isOpen={acessoNegado} onClose={() => setAcessoNegado(false)} />
    </form>
  );
};

// -----------------------------------------------------------------------------
// Modo "Cliente da base"
// -----------------------------------------------------------------------------

// canBase → pode "Avaliar abastecimentos da plataforma" (dados da base RF).
// canExterno → pode "Enviar planilha" (dados de fora da plataforma). São gates por
// AÇÃO: um cliente da base pode ter planilha externa avaliada mesmo sem canBase.
const ClienteMode: FC<{ canBase: boolean; canExterno: boolean }> = ({
  canBase,
  canExterno,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [filter, setFilter] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [selected, setSelected] = useState<MyClient | null>(null);
  const [acessoNegado, setAcessoNegado] = useState(false);

  // Upload de planilha para um cliente já selecionado (abastecimentos fora da plataforma).
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadFileError, setUploadFileError] = useState<string | null>(null);
  const [askProdutoUpload, setAskProdutoUpload] = useState(false);
  const lastUploadVarsRef = useRef<{ file: File; client: MyClient } | null>(null);

  // 1) Descobre o total (e já traz as 30 primeiras — que são TODAS quando ≤30).
  const {
    data: probe,
    isLoading: probeLoading,
    isError: probeError,
  } = useQuery({
    queryKey: ["my-clients", "probe", PROBE_SIZE],
    queryFn: () => myClientsApi.getMyClients(1, PROBE_SIZE),
  });

  const total = probe?.totalItens ?? 0;
  const isInline = !probeLoading && !probeError && total <= INLINE_LIMIT;

  // Debounce do termo de busca (~300ms) — só relevante no modo servidor (>30).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm(filter.trim()), 300);
    return () => clearTimeout(t);
  }, [filter]);

  // 2a) Modo inline (≤30): carrega todas as empresas de uma vez.
  const {
    data: allData,
    isLoading: allLoading,
    isError: allError,
  } = useQuery({
    queryKey: ["my-clients", "all", total],
    queryFn: () => myClientsApi.getMyClients(1, total || PROBE_SIZE),
    enabled: isInline,
  });

  // 2b) Modo servidor (>30): busca por nome/CNPJ com >= 3 caracteres.
  const {
    data: searchData,
    isLoading: searchLoading,
    isFetching: searchFetching,
  } = useQuery({
    queryKey: ["my-clients", "search", debouncedTerm],
    queryFn: () => myClientsApi.getMyClients(1, 20, debouncedTerm),
    enabled:
      !isInline && !probeLoading && !probeError && debouncedTerm.length >= 1,
  });

  // Avaliar abastecimentos da plataforma (Item 2) — puxa da origem, sem upload.
  const avaliarMutation = useMutation<AnalyzeFileResponse, any, MyClient>({
    mutationFn: (client) =>
      postClienteAnalyze({
        customerId: client.customerId,
        razaoSocial: client.name,
        cnpj: client.cpfCnpj || undefined,
      }),
    onSuccess: (resp, client) => {
      navigate(`/resultado/${resp.token}`, {
        state: { fresh: resp, razaoSocial: client.name, cnpj: client.cpfCnpj },
      });
    },
    onError: (err: any) => {
      if (err?.response?.status === 403) {
        setAcessoNegado(true);
        return;
      }
      const msg =
        err?.response?.data?.error ||
        "Não foi possível avaliar o cliente. Tente novamente.";
      toast({ type: "error", message: msg });
    },
  });

  // Enviar planilha de abastecimentos (fora da plataforma) para o cliente selecionado.
  const uploadMutation = useMutation<
    AnalyzeFileResponse,
    any,
    { file: File; client: MyClient; produto?: string }
  >({
    mutationFn: ({ file, client, produto }) =>
      postAnalyzeFile({
        file,
        razaoSocial: client.name,
        cnpj: client.cpfCnpj || undefined,
        produto,
      }),
    onSuccess: (data, { client }) => {
      navigate(`/resultado/${data.token}`, {
        state: { fresh: data, razaoSocial: client.name, cnpj: client.cpfCnpj },
      });
    },
    onError: (err: any, variables) => {
      if (err?.response?.data?.error === "produto_ausente") {
        lastUploadVarsRef.current = { file: variables.file, client: variables.client };
        setAskProdutoUpload(true);
        return;
      }
      if (err?.response?.status === 403) {
        setAcessoNegado(true);
        return;
      }
      const msg =
        err?.response?.data?.error ||
        "Não foi possível analisar o arquivo. Tente novamente.";
      toast({ type: "error", message: msg });
    },
  });

  const selecionar = (client: MyClient) => {
    setSelected(client);
    setUploadFile(null);
    setUploadFileError(null);
  };

  const voltarBusca = () => {
    setSelected(null);
    setUploadFile(null);
    setUploadFileError(null);
  };

  const pickUploadFile = (f: File | null) => {
    if (!f) {
      setUploadFile(null);
      return;
    }
    const err = validateFile(f);
    if (err) {
      setUploadFile(null);
      setUploadFileError(err);
      return;
    }
    setUploadFileError(null);
    setUploadFile(f);
  };

  const enviarPlanilha = () => {
    if (!selected) return;
    if (!uploadFile) {
      setUploadFileError("Envie o arquivo de abastecimentos do cliente.");
      return;
    }
    uploadMutation.mutate({ file: uploadFile, client: selected });
  };

  // Filtro client-side (modo inline): nome OU CNPJ, sem acentuação de máscara.
  const filteredRows = useMemo(() => {
    const rows = allData?.data ?? [];
    const term = filter.trim().toLowerCase();
    if (!term) return rows;
    const digits = term.replace(/\D/g, "");
    return rows.filter((c) => {
      const nameMatch = c.name?.toLowerCase().includes(term);
      const cnpjDigits = (c.cpfCnpj ?? "").replace(/\D/g, "");
      const cnpjMatch = digits.length > 0 && cnpjDigits.includes(digits);
      return nameMatch || cnpjMatch;
    });
  }, [allData, filter]);

  const searchResults = searchData?.data ?? [];
  const isSearching = searchLoading || searchFetching;
  const anyPending = avaliarMutation.isPending || uploadMutation.isPending;

  // --- Cliente selecionado: ações (avaliar plataforma / enviar planilha) -------
  if (selected) {
    return (
      <VStack spacing="20px" align="stretch">
        {/* Overlay bloqueante durante a análise — trava a tela (não dá p/ clicar fora). */}
        {anyPending && (
          <LoadingScreen message="Gerando o estudo…" steps={ESTUDO_STEPS} />
        )}
        <Flex
          align="center"
          justify="space-between"
          px="16px"
          py="14px"
          borderRadius="8px"
          border="1px solid"
          borderColor="neutrals.stroke.soft-200"
          bg="neutrals.surface.surface-0"
        >
          <Box minW={0}>
            <Text
              fontSize="14px"
              fontWeight="600"
              color="neutrals.text.strong-950"
              fontFamily="body"
              isTruncated
            >
              {selected.name}
            </Text>
            <Text fontSize="12px" color="neutrals.text.sub-600" fontFamily="body">
              {selected.cpfCnpj ? maskCnpj(selected.cpfCnpj) : "—"}
            </Text>
          </Box>
          {!anyPending && (
            <Button
              size="sm"
              variant="ghost"
              colorSchema="neutral"
              leftIcon={<Icon as={IconArrowLeft} boxSize={4} />}
              onClick={voltarBusca}
            >
              Trocar cliente
            </Button>
          )}
        </Flex>

        {/* Ação 1: avaliar abastecimentos da plataforma (dados da base → canBase) */}
        {canBase && (
        <Box>
          <Button
            colorSchema="brand"
            variant="solid"
            fullWidth
            isLoading={avaliarMutation.isPending}
            loadingText="Avaliando…"
            spinner={<Spinner size="sm" />}
            isDisabled={uploadMutation.isPending}
            onClick={() => avaliarMutation.mutate(selected)}
          >
            Avaliar abastecimentos da plataforma
          </Button>
          <Text
            mt="6px"
            fontSize="12px"
            color="neutrals.text.sub-600"
            fontFamily="body"
          >
            Usa os abastecimentos que o cliente já fez na plataforma Rede Frota.
          </Text>
        </Box>
        )}

        {/* Ação 2: enviar planilha de abastecimentos (fora da plataforma → canExterno) */}
        {canExterno && (
        <Box>
          <Text
            fontSize="14px"
            fontWeight="600"
            color="neutrals.text.strong-950"
            fontFamily="body"
            mb="4px"
          >
            Enviar planilha de abastecimentos
          </Text>
          <Text
            fontSize="12px"
            color="neutrals.text.sub-600"
            fontFamily="body"
            mb="12px"
          >
            Use quando o time comercial obteve abastecimentos fora da plataforma
            para avaliar.
          </Text>

          <Dropzone
            file={uploadFile}
            onPick={pickUploadFile}
            fileError={uploadFileError}
            isDisabled={uploadMutation.isPending}
            required={false}
          />

          <Button
            colorSchema="brand"
            variant="outline"
            fullWidth
            mt="12px"
            isLoading={uploadMutation.isPending}
            loadingText="Analisando…"
            spinner={<Spinner size="sm" />}
            isDisabled={avaliarMutation.isPending}
            onClick={enviarPlanilha}
          >
            Efetuar análise da planilha
          </Button>
        </Box>
        )}

        <ProdutoModal
          isOpen={askProdutoUpload}
          onClose={() => setAskProdutoUpload(false)}
          onConfirm={(produto) => {
            setAskProdutoUpload(false);
            if (lastUploadVarsRef.current)
              uploadMutation.mutate({ ...lastUploadVarsRef.current, produto });
          }}
        />

        <AcessoNegadoModal isOpen={acessoNegado} onClose={() => setAcessoNegado(false)} />
      </VStack>
    );
  }

  // --- Busca de cliente --------------------------------------------------------
  return (
    <Box>
      {anyPending && (
        <LoadingScreen message="Gerando o estudo…" steps={ESTUDO_STEPS} />
      )}
      {/* Enquanto descobrimos o total, mostra um carregando neutro. */}
      {probeLoading && (
        <Flex align="center" justify="center" py={16} gap={3}>
          <Spinner color="primary.light.base" size="sm" />
          <Text color="neutrals.text.sub-600" fontFamily="body">
            Carregando…
          </Text>
        </Flex>
      )}

      {probeError && (
        <Text fontSize="sm" color="neutrals.text.sub-600" fontFamily="body">
          Não foi possível carregar seus clientes. Tente novamente em instantes.
        </Text>
      )}

      {/* Modo inline (≤30): lista completa + filtro client-side. */}
      {!probeLoading && !probeError && isInline && (
        <>
          <Box maxW="420px" mb="16px">
            <AppInput
              placeholder="Filtrar por nome ou CNPJ"
              value={filter}
              onChange={setFilter}
            />
          </Box>

          {allLoading ? (
            <Flex align="center" justify="center" py={10} gap={3}>
              <Spinner color="primary.light.base" size="sm" />
              <Text color="neutrals.text.sub-600" fontFamily="body" fontSize="sm">
                Carregando…
              </Text>
            </Flex>
          ) : (
            <Box
              maxW="640px"
              borderRadius="8px"
              border="1px solid"
              borderColor="neutrals.stroke.soft-200"
              bg="neutrals.surface.surface-0"
              overflow="hidden"
            >
              {filteredRows.length === 0 ? (
                <Flex align="center" justify="center" py={6}>
                  <Text
                    color="neutrals.text.sub-600"
                    fontFamily="body"
                    fontSize="sm"
                  >
                    {allError
                      ? "Não foi possível carregar."
                      : "Nenhuma empresa encontrada."}
                  </Text>
                </Flex>
              ) : (
                filteredRows.map((client) => (
                  <ClienteRow
                    key={client.customerId}
                    client={client}
                    onSelect={selecionar}
                  />
                ))
              )}
            </Box>
          )}
        </>
      )}

      {/* Modo servidor (>30): busca com dropdown suspenso. */}
      {!probeLoading && !probeError && !isInline && (
        <Box maxW="640px" position="relative">
          <AppInput
            placeholder="Buscar empresa por nome ou CNPJ"
            value={filter}
            onChange={setFilter}
          />

          {debouncedTerm.length < 1 ? (
            <Text
              mt="8px"
              fontSize="sm"
              color="neutrals.text.soft-400"
              fontFamily="body"
            >
              Digite ao menos 1 caractere para buscar por nome ou CNPJ.
            </Text>
          ) : (
            <Box
              mt="8px"
              borderRadius="8px"
              border="1px solid"
              borderColor="neutrals.stroke.soft-200"
              bg="neutrals.surface.surface-0"
              overflow="hidden"
            >
              {isSearching ? (
                <Flex align="center" justify="center" py={6} gap={3}>
                  <Spinner color="primary.light.base" size="sm" />
                  <Text
                    color="neutrals.text.sub-600"
                    fontFamily="body"
                    fontSize="sm"
                  >
                    Buscando…
                  </Text>
                </Flex>
              ) : searchResults.length === 0 ? (
                <Flex align="center" justify="center" py={6}>
                  <Text
                    color="neutrals.text.sub-600"
                    fontFamily="body"
                    fontSize="sm"
                  >
                    Nenhuma empresa encontrada
                  </Text>
                </Flex>
              ) : (
                searchResults.map((client) => (
                  <ClienteRow
                    key={client.customerId}
                    client={client}
                    onSelect={selecionar}
                  />
                ))
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

interface ClienteRowProps {
  client: MyClient;
  onSelect: (client: MyClient) => void;
}

const ClienteRow: FC<ClienteRowProps> = ({ client, onSelect }) => (
  <Flex
    align="center"
    justify="space-between"
    gap={3}
    px="16px"
    py="12px"
    borderBottom="1px solid"
    borderColor="neutrals.stroke.soft-200"
    _last={{ borderBottom: "none" }}
    _hover={{ bg: "neutrals.background.weak-50" }}
  >
    <Box minW={0}>
      <Text
        fontSize="14px"
        fontWeight="500"
        color="neutrals.text.strong-950"
        fontFamily="body"
        isTruncated
      >
        {client.name}
      </Text>
      <Text fontSize="12px" color="neutrals.text.sub-600" fontFamily="body">
        {client.cpfCnpj ? maskCnpj(client.cpfCnpj) : "—"}
      </Text>
    </Box>
    <Button
      size="sm"
      variant="solid"
      colorSchema="brand"
      onClick={() => onSelect(client)}
    >
      Selecionar
    </Button>
  </Flex>
);

// -----------------------------------------------------------------------------
// Abas de modo (padrão sublinhado — inspirado na Gestão de Carteiras)
// -----------------------------------------------------------------------------

interface ModoTabProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

const ModoTab: FC<ModoTabProps> = ({ label, isActive, onClick }) => (
  <Box
    as="button"
    type="button"
    onClick={onClick}
    px="4px"
    pb="12px"
    mb="-1px"
    fontSize="14px"
    fontFamily="body"
    fontWeight={isActive ? "600" : "500"}
    color={isActive ? "primary.light.base" : "neutrals.text.sub-600"}
    borderBottom="2px solid"
    borderColor={isActive ? "primary.light.base" : "transparent"}
    transition="color 0.15s, border-color 0.15s"
    _hover={{ color: isActive ? "primary.light.base" : "neutrals.text.strong-950" }}
  >
    {label}
  </Box>
);

// -----------------------------------------------------------------------------
// Página
// -----------------------------------------------------------------------------

export const NovaConsultaPage: FC = () => {
  usePageTitle("Nova Análise");
  const { canBase, canExterno } = useAcesso();

  // Aba "Nova transportadora" (prospect) é 100% dados externos → só com canExterno.
  // Aba "Cliente da base" agrega DUAS ações: avaliar plataforma (canBase) e enviar
  // planilha externa (canExterno); aparece se o usuário tiver qualquer uma delas.
  const showProspect = canExterno;
  const showCliente = canBase || canExterno;

  // Modo inicial recai numa aba que o usuário realmente pode usar.
  const [modo, setModo] = useState<Modo>(showProspect ? "prospect" : "cliente");
  // Se as roles carregarem depois e o modo atual não estiver disponível, corrige.
  useEffect(() => {
    if (modo === "prospect" && !showProspect) setModo("cliente");
    if (modo === "cliente" && !showCliente) setModo("prospect");
  }, [modo, showProspect, showCliente]);

  return (
    <Box maxW="640px">
      <Heading
        fontSize="20px"
        fontFamily="heading"
        color="neutrals.text.strong-950"
      >
        Nova análise
      </Heading>
      <Text mt="4px" fontSize="sm" color="neutrals.text.sub-600" fontFamily="body">
        Gere o diagnóstico de rotas de uma nova transportadora ou de um cliente da
        base.
      </Text>

      {/* Abas de modo — só as que o usuário pode usar */}
      <HStack
        spacing="28px"
        mt="24px"
        mb="24px"
        borderBottom="1px solid"
        borderColor="neutrals.stroke.soft-200"
        align="stretch"
      >
        {showProspect && (
          <ModoTab
            label="Nova transportadora"
            isActive={modo === "prospect"}
            onClick={() => setModo("prospect")}
          />
        )}
        {showCliente && (
          <ModoTab
            label="Cliente da base"
            isActive={modo === "cliente"}
            onClick={() => setModo("cliente")}
          />
        )}
      </HStack>

      {modo === "prospect" ? (
        <ProspectMode />
      ) : (
        <ClienteMode canBase={canBase} canExterno={canExterno} />
      )}
    </Box>
  );
};
